import { Link, useLocation, useNavigate } from "react-router-dom";
import { Star, Award } from "lucide-react";
import { fileUrl } from "@/lib/api";
import Money from "@/components/Money";
import useFeaturedHost from "@/lib/useFeaturedHost";
import CarCardBadges from "@/components/car/CarCardBadges";
import { carTypeBadgeClass } from "@/lib/carTypeBadge";

const FALLBACK = "https://images.unsplash.com/photo-1576933765009-9f57f43910aa?auto=format&fit=crop&w=900&q=70";

// Iter 67: forward the search-engine selections (pickup_location, dropoff_location,
// start_date, end_date) from the current URL into the car-detail link so the
// BookingWidget on /cars/:id can pre-fill what the user already typed on Home/Browse.
const FORWARDED_PARAMS = ["pickup_location", "dropoff_location", "start_date", "end_date", "country"];
function buildCarLink(carId, search) {
  const incoming = new URLSearchParams(search || "");
  const out = new URLSearchParams();
  for (const k of FORWARDED_PARAMS) {
    const v = incoming.get(k);
    if (v) out.set(k, v);
  }
  const qs = out.toString();
  return qs ? `/cars/${carId}?${qs}` : `/cars/${carId}`;
}

export default function CarCard({ car, index = 0 }) {
  const firstImage = car.images?.[0];
  const src = firstImage ? (firstImage.startsWith("http") ? firstImage : fileUrl(firstImage)) : FALLBACK;
  const location = useLocation();
  const navigate = useNavigate();
  const href = buildCarLink(car.id, location.search);
  // Iter 70i — vendor deep-link handler. Because CarCard is wrapped in a
  // `<Link>` we cannot nest a second anchor for the vendor logo/name.
  // Instead we intercept the click, stop it from bubbling to the outer
  // Link, and navigate programmatically to `/vendors/{public_slug}`.
  const gotoVendor = (e) => {
    if (!car.vendor?.public_slug) return;
    e.preventDefault();
    e.stopPropagation();
    navigate(`/vendors/${car.vendor.public_slug}`);
  };
  // Iter 67af — pin the "Featured host" pill on cars that belong to this
  // week's featured host. The hook shares one fetch across all CarCards.
  const { vendorId: featuredVendorId } = useFeaturedHost();
  const isFeaturedHost = featuredVendorId && car.vendor_id === featuredVendorId;
  const isFeaturedCar = !!car.is_featured;
  return (
    <Link
      to={href}
      className="group flex flex-col gap-3 fade-up"
      style={{ animationDelay: `${Math.min(index * 60, 600)}ms` }}
      data-testid={`car-card-${car.id}`}
    >
      <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-100 relative">
        <img
          src={src}
          alt={`${car.make} ${car.model}`}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
          onError={(e) => { e.currentTarget.src = FALLBACK; }}
        />
        <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
          <div
            className={`backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold border ${carTypeBadgeClass(car.car_type)}`}
            data-testid={`carcard-type-${car.id}`}
          >
            {car.car_type}
          </div>
          {isFeaturedCar && (
            <div
              className="inline-flex items-center gap-1 bg-gradient-to-r from-amber-400 to-yellow-300 text-amber-950 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] shadow-md"
              data-testid={`carcard-featured-listing-${car.id}`}
              title="Featured on the homepage"
            >
              <Award className="h-3 w-3" /> Featured
            </div>
          )}
          {isFeaturedHost && !isFeaturedCar && (
            <div
              className="inline-flex items-center gap-1 bg-amber-400 text-amber-950 rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em] shadow-md"
              data-testid={`carcard-featured-${car.id}`}
              title="Featured host of the week"
            >
              <Award className="h-3 w-3" /> Featured host
            </div>
          )}
        </div>
        {car.vendor?.business_logo && (
          <button
            type="button"
            onClick={gotoVendor}
            className="absolute top-3 right-3 h-9 w-9 rounded-full bg-white/95 backdrop-blur-sm border border-white/80 shadow-sm overflow-hidden flex items-center justify-center hover:scale-110 hover:shadow-md transition-transform"
            title={car.vendor?.business_name || car.vendor?.name || "Vendor"}
            data-testid={`carcard-logo-${car.id}`}
          >
            <img
              src={car.vendor.business_logo.startsWith("http") ? car.vendor.business_logo : fileUrl(car.vendor.business_logo)}
              alt={car.vendor?.business_name || "Vendor"}
              className="h-full w-full object-contain"
            />
          </button>
        )}
      </div>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-neutral-900 truncate">{car.make} {car.model} <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-50 ring-1 ring-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider align-middle">or similar</span></div>
          <div className="text-sm text-neutral-500 truncate">{car.location} · {car.year}</div>
          {/* Iter 74n — vendor name text hidden on card views per user request
              to reduce clutter. The clickable logo overlay above still routes
              to the vendor microsite. */}
        </div>
        <div className="flex items-center gap-1 text-sm shrink-0">
          <Star className="h-4 w-4 fill-neutral-900 text-neutral-900" />
          <span className="font-medium">{car.rating > 0 ? car.rating.toFixed(1) : "New"}</span>
          {car.review_count > 0 && <span className="text-neutral-500">({car.review_count})</span>}
        </div>
      </div>
      <div className="text-sm">
        <Money amount={car.price_per_day} className="font-semibold text-neutral-900" testId={`carcard-price-${car.id}`} />
        <span className="text-neutral-500"> / day</span>
      </div>
      <CarCardBadges
        car={car}
        showPayments={false}
        testIdPrefix={`carcard-perks-${car.id}`}
      />
    </Link>
  );
}
